import * as React from 'react'
import { Command, CommandGroup, CommandItem, CommandList } from '@components/common/command'
import { Command as CommandPrimitive } from 'cmdk'
import { useAuth } from '@stores/auth-store'
import { DictType } from '@pma/types'
import { Avatar, AvatarImage } from '@components/common/avatar'
import { Button } from '@components/common/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@components/common/tooltip'
import useTranslator from '@hooks/use-translator'

interface Props {
  setUsers: any
}
export function UsersMultiSelect({ setUsers }: Props) {
  const inputRef = React.useRef<HTMLInputElement>(null)
  const [open, setOpen] = React.useState(false)
  const [selected, setSelected] = React.useState<DictType[]>([])
  const [inputValue, setInputValue] = React.useState('')

  const { authConfig } = useAuth()
  const { translate } = useTranslator()

  const organizationUsers = authConfig?.organizationUsers

  const handleUnselect = React.useCallback((user: DictType) => {
    setSelected((prev) => prev.filter((s) => s.name !== user.name))
  }, [])

  const handleKeyDown = React.useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    const input = inputRef.current
    if (input) {
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (input.value === '') {
          setSelected((prev) => {
            const newSelected = [...prev]
            newSelected.pop()
            return newSelected
          })
        }
      }

      if (e.key === 'Escape') {
        input.blur()
      }
    }
  }, [])

  const availableUsers =
    organizationUsers?.length > 0 &&
    organizationUsers
      ?.filter((user: DictType) => !selected.includes(user))
      .filter((u: DictType) => u.name)

  return (
    <Command onKeyDown={handleKeyDown} className='overflow-visible bg-transparent'>
      <div className='group rounded-md border border-input px-3 py-2 text-sm ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2'>
        <div className='flex flex-wrap items-center justify-start gap-1'>
          {selected.map((user) => {
            return (
              <Tooltip>
                <TooltipTrigger>
                  <Button
                    size={'icon'}
                    key={user.id}
                    variant='ghost'
                    className='-ml-4 rounded-full p-0 outline-none ring-offset-background transition-all ease-out hover:z-10 focus:ring-2 focus:ring-ring focus:ring-offset-2'
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleUnselect(user)
                      }
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                    }}
                    onClick={() => handleUnselect(user)}
                  >
                    <Avatar className='relative h-9 w-9'>
                      <AvatarImage
                        src={
                          user?.user_picture?.url || `${process.env.PUBLIC_URL}/user-profile.svg`
                        }
                      />
                    </Avatar>
                  </Button>
                </TooltipTrigger>
                <TooltipContent className='flex items-center gap-1'>
                  <h4> {user.name}</h4>
                </TooltipContent>
              </Tooltip>
            )
          })}

          <CommandPrimitive.Input
            ref={inputRef}
            value={inputValue}
            onValueChange={setInputValue}
            onBlur={() => setOpen(false)}
            onFocus={() => setOpen(true)}
            placeholder={translate('Select users') + '...'}
            className='ml-2 flex-1 bg-transparent outline-none placeholder:text-muted-foreground'
          />
        </div>
      </div>
      <div className='relative mt-2'>
        <CommandList>
          {open && availableUsers?.length > 0 ? (
            <div className='animate-in absolute top-0 z-10 w-full rounded-md border bg-popover text-popover-foreground shadow-md outline-none'>
              <CommandGroup className='h-full max-h-[400px] overflow-y-auto'>
                {availableUsers?.map((user: DictType) => {
                  return (
                    <CommandItem
                      key={user.value}
                      onMouseDown={(e: any) => {
                        e.preventDefault()
                        e.stopPropagation()
                      }}
                      onSelect={(value: any) => {
                        setInputValue('')
                        setSelected((prev) => [...prev, user])
                        setUsers([...selected, user])
                      }}
                      className={'my-1 flex cursor-pointer items-center gap-2'}
                    >
                      <Avatar className='h-7 w-7'>
                        <AvatarImage
                          src={
                            user?.user_picture?.url || `${process.env.PUBLIC_URL}/user-profile.svg`
                          }
                        />
                      </Avatar>{' '}
                      {user.name}
                    </CommandItem>
                  )
                })}
              </CommandGroup>
            </div>
          ) : null}
        </CommandList>
      </div>
    </Command>
  )
}
